import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import SectionLoader from '../Loader/SectionLoader';
import BasicModalBody from '../BasicModal/BasicModalBody';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import config from '../../config';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import { getCurrentAccountUid } from '../../store/selectors/accountSelectors';
import paymentTypes, {
  PAYMENT_METHOD_TYPES_ICONS,
} from '../../util/enum/api/paymentTypes';
import { ORDER_ENTRY_TYPES } from '../../util/enum/api/orderEntryTypes';
import {
  emptyQuotesOrderStorageBuy,
  executeQuotesForOrderStorageBuy,
  getQuotesForBuyDelivery,
  getQuotesForOrderStorageBuy,
} from '../../store/actions/orders/orderActions';
import { COUNTDOWN_TIMER_SEC } from '../../constants/timerConstants';
import useStartTimer from '../../util/hooks/useStartTimer';
import { handleExpiration } from '../../util/helpers/timerHelpers';
import PriceLockTimer from '../Common/Timers/PriceLockTimer';
import OrderReviewPriceList from './OrderReviewPriceList';
import { ReactComponent as InfoIcon } from '../../assets/images/svg/info-ico.svg';
import { ReactComponent as HandCoinsIcon } from '../../assets/images/svg/hand-coins.svg';
import Paragraph from '../Paragraph/Paragraph';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import RowItemElement from './RowItem';
import SelectPaymentOptions from './SelectPaymentOptions';
import ModalButtons from '../Modal/ModalButtons';
import PriceTiers from './PriceTiers';
import {
  accountTransactionsFetch,
  fetchPortfolioProductBySymbol,
} from '../../store/actions/portfolio/portfolioActions';
import { selectBalancesAvailable } from '../../store/selectors/portfolioBalancesSelectors';
import { PRODUCT_TRANSACTIONS } from '../../constants/accountTransactionConstants';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACH_DEPOSIT_WIZARD,
  IRA_DEPOSIT_WIZARD,
  IS_FUND_MODAL_OPEN,
  SIGNIFYD_SESSION_ID,
  STANDARD_DEPOSIT_WIZARD,
} from '../../constants/sessionStorage';
import { loadSignifydDeviceFingerprintScriptWithoutSessionId } from '../../signifydDeviceFingerprint';
import { ALL_ZEROES_UID } from '../../constants/mockScenarios';
import useCheckPermissions from '../../util/hooks/useCheckPermissions';
import StandardDepositWizard from '../Modals/Deposit/StandardDepositWizard/StandardDepositWizard';
import IraDepositWizard from '../Modals/DepositWizard/IraDepositWizard';
import MilleniumDepositWizard from '../Modals/DepositWizard/MilleniumDepositWizard';
import { IRA_ENTRUST_TYPE, IRA_EQUITY_TYPE } from '../../util/constants';
import { deleteWizardKey } from '../../util/helpers/wizardHelpers';
import SuccessToast from './components/SuccessToast';
import { postPaidApiErrors } from '../../util/enum/api/postPaidApiErrors';
import useErrorDrawer from '../../util/hooks/useErrorDrawer';
import ErrorDrawer from './ErrorDrawer';
import AnchorOnClick from '../Anchor/AnchorOnClick';

const ProductImage = styled.img`
  width: ${pxToRem(150)};
  object-fit: contain;
  margin-bottom: ${pxToRem(10)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    width: 60%;
  }
`;

const ProductInfoWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProductName = styled.h2`
  font-size: ${pxToRem(20)};
  font-weight: 400;
  color: ${themeColors.colorTextPrimary};
`;

const SavingInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.isCCSelected
      ? themeColors.colorBackgroundSecondary
      : themeColors.colorGreenDarkOpacity};
  border: 1px solid
    ${(props) =>
      props.isCCSelected
        ? themeColors.colorPrimaryLight
        : themeColors.colorBadgeSuccess};
  padding: ${pxToRem(4)};
  border-radius: ${variables.borderRadius.borderRadius};
  margin-bottom: ${pxToRem(12)};

  ${Paragraph} {
    font-size: ${pxToRem(14)};
    color: ${(props) =>
      props.isCCSelected
        ? themeColors.colorTextSecondary
        : themeColors.colorSuccess};
    font-weight: 400;
    margin-left: ${pxToRem(6)};
  }
`;

const ButtonWrapper = styled.div`
  button {
    height: ${pxToRem(44)};
  }

  ${mediaBelow(variables.breakpoints.bpSm)} {
    button {
      height: ${pxToRemMd(44)};
    }
  }
`;

const OrderReviewPriceListWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ModalLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;

  @media (max-width: 1020px) and (max-height: 450px) and (orientation: landscape) {
    overflow: visible;
    height: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const ScrollableMiddle = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  margin-bottom: ${pxToRem(12)};
`;

const uLayoutStyle = css`
  height: 100%;
  overflow: hidden;
`;

const BuyModalDimmer = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.45);
  z-index: 9999999;
`;

const OrderReview = ({
  wizardData,
  setWizardData,
  isPostPaidVisible,
  setWizardTitle,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  isProductPage,
  handleNext,
  setGoBackHandler,
  handleBack,
  isSegregated,
  isDeliveryOnlyAccount,
  openSupport,
  closeSupport,
  isSupportOpen,
}) => {
  const { t } = useTranslation();
  const {
    storageProduct,
    // selectedPaymentMethod,
    selectedVault,
    calculation,
    expiryTime,
    isVaultActiveTab,
    selectedAddress,
    updatedPaymentMethods,
    quoteID,
    selectedPlan,
  } = wizardData;
  const dispatch = useDispatch();
  const settings = useSelector(selectSettings);
  const accountUid = useSelector(getCurrentAccountUid);
  const { RootUrl } = useSelector(selectSettings);
  const balance = useSelector(selectBalancesAvailable);
  const reduxPaymentMethods = balance?.PaymentMethods;
  const [livePaymentMethods, setLivePaymentMethods] = useState(
    updatedPaymentMethods,
  );

  useEffect(() => {
    if (!reduxPaymentMethods?.length) return;
    const mapped = reduxPaymentMethods.map((pm) =>
      pm.PaymentMethodUid ? pm : { ...pm, PaymentMethodUid: 1 },
    );
    setLivePaymentMethods(mapped);
  }, [reduxPaymentMethods]);

  const imageSource = config.algoliaSearch.imageCode
    .replace('{product_code}', storageProduct?.ProductCode)
    .replace('{base_url}', RootUrl);
  const prevPaymentMethodRef = useRef(selectedPaymentMethod);
  const [isLoadingGetQuotes, setIsLoadingGetQuotes] = useState(false);
  const [isFundsModalOpen, setIsFundsModalOpen] = useState(false);
  const [counter, setCounter] = useState(COUNTDOWN_TIMER_SEC);
  const [unity, setUnity] = useState(false);
  const [expirationMs, setExpirationMs] = useState(0);
  const [quoteUid, setQuoteUid] = useState(quoteID || '');
  const [paymentMethodData, setPaymentMethodData] = useState(
    calculation?.paymentMethodStepData || {},
  );
  const [isAddPaymentMethodOpen, setIsAddPaymentMethodOpen] = useState(false);
  // Guard against stale `onHandleGetQuotesSuccess` callbacks firing after this component unmounts.
  // When the user navigates back from the review screen, this component is unmounted by StepByStep,
  // but any in-flight Quotes API call (e.g. from the 30-second refresh timer) may still resolve.
  // Without this guard, the stale callback would call `setWizardData` with the old order values,
  // overwriting the parent's shared wizardData and corrupting the next step's payload.
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const {
    displayIRAEntrustOptionsPermission,
    displayIRAMilleniumOptionsPermission,
    displayIRAEquityOptionsPermission,
    displayStandardOptionsPermission,
  } = useCheckPermissions();
  const {
    isOpen: isErrorOpen,
    errorTitle,
    errorMessage,
    showError,
    hideError,
  } = useErrorDrawer();

  const isSegregatedProduct = !isProductPage
    ? selectedPlan === 'premium'
    : isSegregated;

  const paymentMethodInfo =
    selectedPaymentMethod?.PaymentMethodType === 3
      ? paymentMethodData?.DataWithCCFee
      : paymentMethodData?.DataWithoutCCFee;

  const paymentDiscountValue1 = calculation?.paymentTypeDiscount;
  const paymentDiscountValue2 =
    paymentMethodData?.DataWithCCFee?.TotalClientExecutionPrice -
    paymentMethodData?.DataWithoutCCFee?.TotalClientExecutionPrice;
  const paymentDiscountValue =
    paymentDiscountValue1 !== null
      ? paymentDiscountValue1
      : paymentDiscountValue2;

  const [paymentDiscount, setPaymentDiscount] = useState(
    paymentDiscountValue || 0,
  );

  const [openSelectPaymentOptions, setOpenSelectPaymentOptions] = useState(
    false,
  );
  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const [openPriceTiersDrawer, setOpenPriceTiersDrawer] = useState(false);
  const [isLoadingExecuteQuotes, setIsLoadingExecuteQuotes] = useState(false);
  const sessionId = retrieveFromSessionStorage(SIGNIFYD_SESSION_ID);
  const [successToast, setSuccessToast] = useState({
    isOpen: false,
    title: '',
    message: '',
  });

  const quantityExecuted = isPostPaidVisible
    ? paymentMethodInfo?.QuantityExecuted
    : calculation?.quantity;

  const deliveryLocation =
    !isProductPage &&
    storageProduct?.Locations.find((location) => location.LocationType === 12);

  useEffect(() => {
    const stableBackHandler = () => {
      handleBack();
    };

    setGoBackHandler(() => stableBackHandler);

    return () => {
      setGoBackHandler(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setGoBackHandler]);

  useEffect(() => {
    setWizardTitle(t('buyWizard.reviewBuy'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!expirationMs) {
      if (expiryTime) {
        handleExpiration(expiryTime, setExpirationMs);
      }
    }
  }, [expirationMs, expiryTime]);

  useEffect(() => {
    const prev = prevPaymentMethodRef.current;
    const curr = selectedPaymentMethod;

    const hasChanged =
      prev?.PaymentMethodUid !== curr?.PaymentMethodUid ||
      prev?.PaymentMethodType !== curr?.PaymentMethodType;

    if (hasChanged && curr) {
      fetchQuotes();
    }

    prevPaymentMethodRef.current = curr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentMethod]);

  const totalPrice = useCallback(() => {
    if (isPostPaidVisible) {
      if (calculation?.salesTax > 0 || calculation?.deliveryFee > 0) {
        return (
          paymentMethodInfo?.TotalClientExecutionPrice +
          calculation?.salesTax +
          calculation?.deliveryFee
        );
      }
      return paymentMethodInfo?.TotalClientExecutionPrice;
    }
    if (calculation?.salesTax > 0 || calculation?.deliveryFee > 0) {
      return (
        calculation?.estimatedOrderTotal +
        calculation?.salesTax +
        calculation?.deliveryFee
      );
    }
    return calculation?.estimatedOrderTotal;
  }, [calculation, paymentMethodInfo, isPostPaidVisible]);

  const onHandleGetQuotesSuccess = (getQuoteResult) => {
    if (!isMountedRef.current) return;
    const {
      QuoteUid,
      ExpiresOnUtc,
      Tiers,
      QuoteStatusItem: {
        QuantityExecuted,
        ClientPricePerUnit,
        TotalClientExecutionPrice,
        VolumeDiscount,
        PaymentTypeDiscount,
        PaymentMethodStepData,
        DeliveryFee,
        SalesTax,
        Subtotal,
      },
    } = getQuoteResult;

    const calculation = {
      estimatedOrderTotal: TotalClientExecutionPrice,
      newPricePerUnit: ClientPricePerUnit,
      volumeDiscount: VolumeDiscount,
      quantity: QuantityExecuted,
      paymentTypeDiscount: PaymentTypeDiscount,
      paymentMethodStepData: PaymentMethodStepData,
      deliveryFee: DeliveryFee,
      salesTax: SalesTax,
      productTiers: Tiers,
      subTotal: Subtotal,
    };
    setQuoteUid(QuoteUid);
    // setPricePerUnit(ClientPricePerUnit);
    // setQuantity(QuantityExecuted);
    // setClientExecutionPrice(TotalClientExecutionPrice);
    // setVolumeDiscount(VolumeDiscount);
    handleExpiration(ExpiresOnUtc, setExpirationMs);
    setPaymentDiscount(PaymentTypeDiscount);
    setPaymentMethodData(PaymentMethodStepData);

    setWizardData({
      ...wizardData,
      calculation,
      quoteID: QuoteUid,
      expiryTime: ExpiresOnUtc,
    });
  };

  const counterUpdater = useCallback((epoch) => {
    if (epoch?.data) {
      const [counter, unity] = epoch.data;
      setCounter((prevCounter) => prevCounter - prevCounter + counter);
      setUnity(unity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchQuotes = () => {
    const paymentTypeUid =
      selectedPaymentMethod?.PaymentMethodType !== 1
        ? selectedPaymentMethod?.PaymentMethodUid
        : ALL_ZEROES_UID;
    const payload = {
      getQuotesPayload: {
        accountUid,
        symbolCode: selectedVault?.SymbolCode,
        quantityOrAmount:
          wizardData?.activeType === 'dollars'
            ? wizardData?.orderValues?.CashAmount
            : wizardData?.orderValues?.QuantityAmount,
        sideType: 'Buy',
        unitType: wizardData?.activeType === 'dollars' ? 'Cash' : 'Quantity',
        paymentType: paymentTypes[selectedPaymentMethod?.PaymentMethodType],
        addressUid: selectedAddress?.AddressUid,
      },
      isLoading: setIsLoadingGetQuotes,
      onSuccess: onHandleGetQuotesSuccess,
      onError: onHandleGetQuotesError,
      isSegregated: isSegregatedProduct,
      paymentTypeUid,
      addPaymentMethodData: true,
      orderEntryType: ORDER_ENTRY_TYPES.VERIFICATION,
    };
    setIsLoadingGetQuotes(true);
    if (isVaultActiveTab) {
      dispatch(
        getQuotesForOrderStorageBuy({
          ...payload,
        }),
      );
    } else {
      dispatch(
        getQuotesForBuyDelivery({
          ...payload,
        }),
      );
    }
  };

  useEffect(() => {
    if (counter === 2 && selectedVault?.SymbolCode) {
      fetchQuotes();
      setCounter(COUNTDOWN_TIMER_SEC);
    }

    return () => dispatch(emptyQuotesOrderStorageBuy());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    counter,
    accountUid,
    dispatch,
    wizardData?.activeType,
    selectedVault?.SymbolCode,
  ]);

  useStartTimer(
    expirationMs,
    counterUpdater,
    isLoadingGetQuotes || isLoadingExecuteQuotes,
  );

  const handleOpenPriceTiers = useCallback(() => {
    setOpenPriceTiersDrawer(true);
  }, [setOpenPriceTiersDrawer]);

  function paymentMethodRowItem() {
    if (selectedPaymentMethod) {
      if (selectedPaymentMethod?.PaymentMethodType === 1) {
        return t('buyWizard.payment.paymentMethods.AccountBalance');
      }
      if (selectedPaymentMethod?.PaymentMethodType === 2) {
        return `${selectedPaymentMethod?.BankName}, ${'***'}${
          selectedPaymentMethod?.BankAccountMask
        }`;
      }
      if (selectedPaymentMethod?.PaymentMethodType === 3) {
        return `${t('buyWizard.payment.paymentMethods.CreditCard')} ${'***'}${
          selectedPaymentMethod?.Last4Digits
        }`;
      }
    } else {
      return t('buyWizard.payment.emptyPaymentRow');
    }
  }

  useEffect(() => {
    const sessionId = retrieveFromSessionStorage(SIGNIFYD_SESSION_ID);
    const existingScript = document.getElementById('sig-api');
    if (!existingScript) {
      loadSignifydDeviceFingerprintScriptWithoutSessionId(sessionId);
    }
  }, []);

  const onConfirmOrder = () => {
    setWizardTitle(t('buyWizard.buyForStorage.title'));
    handleNext();
  };

  const onHandleExecuteQuotesSuccess = (getExecuteQuoteResult) => {
    const { TransactionUid } = getExecuteQuoteResult;

    setWizardData({
      ...wizardData,
      transactionUid: TransactionUid,
    });
    onConfirmOrder();
    // setCloseIconVisible(true);
    // verifyOrderEcommerceGtmHelper({
    //   ecommerceGtmEvent,
    //   wizardData,
    //   finalOrder,
    //   account,
    //   orderSideType: 'buy',
    //   quantity,
    //   isForDelivery: false,
    //   orderCode: quoteUid || quoteID,
    // });
  };

  const onHandleSuccess = () => {
    // verifyOrderEcommerceGtmHelper({
    //   ecommerceGtmEvent,
    //   wizardData,
    //   finalOrder,
    //   account,
    //   orderSideType: 'buy',
    //   quantity,
    //   isForDelivery: false,
    //   orderCode,
    // });

    if (isProductPage) {
      dispatch(
        fetchPortfolioProductBySymbol({
          accountUid,
          symbol: selectedVault.SymbolCode,
          isSegregated,
        }),
      );
      dispatch(
        accountTransactionsFetch({
          accountUid,
          productSymbol: selectedVault.SymbolCode,
          pageNumber: 0,
          pageSize: 20,
          content: PRODUCT_TRANSACTIONS,
        }),
      );
    }
  };

  const onHandleGetQuotesError = (error) => {
    const errorCode = error?.response?.data?.Errors[0]?.Code;

    if (
      !isVaultActiveTab &&
      errorCode === 'BuyForDeliveryAmountMinimumIsNotReached'
    ) {
      showError({
        message: t('apiErrors.BuyForDeliveryAmountMinimumIsNotReached', {
          orderMinimum:
            selectedAddress?.StateRegion === 'NY'
              ? `$${settings?.BuyForDeliveryMinimums[0].Amount}`
              : `$${deliveryLocation?.BuyForDeliveryMinimumAmount}`,
        }),
      });
      return;
    }

    if (
      errorCode === 'BuyMinimumAmountIsNotReached' ||
      errorCode === 'OrderMinimumValidationError'
    ) {
      showError({
        message: t('apiErrors.BuyForDeliveryAmountMinimumIsNotReached', {
          orderMinimum: selectedVault?.BuyMinimumAmount
            ? `$${selectedVault?.BuyMinimumAmount}`
            : wizardData?.storageProduct?.Locations[0]?.BuyMinimumAmount,
        }),
      });
      return;
    }

    if (
      errorCode === 'OrderMaximumValidationError' &&
      selectedPaymentMethod?.PaymentMethodType === 2
    ) {
      showError({
        title: t('buyWizard.achOrCardLimitExceededErrorTitle'),
        message: t('buyWizard.achLimitExceededError', {
          phoneTollFree: settings?.SupportPhoneTollFree,
          phoneInternational: settings?.SupportPhoneInternational,
        }),
      });
      return;
    }

    if (
      errorCode === 'OrderMaximumValidationError' &&
      selectedPaymentMethod?.PaymentMethodType === 3
    ) {
      showError({
        title: t('buyWizard.achOrCardLimitExceededErrorTitle'),

        message: t('buyWizard.cardLimitExceededError', {
          phoneTollFree: settings?.SupportPhoneTollFree,
          phoneInternational: settings?.SupportPhoneInternational,
        }),
      });
      return;
    }

    showError({ message: t(`apiErrors.${errorCode}`) });
  };

  const postPaidErrorMessage = () => (
    <div>
      <Paragraph marginBottom={12}>
        {t('buyWizard.postPaid.paymentFailureParagraph1', {
          paymentType: 'ACH bank connection.',
        })}
      </Paragraph>
      <Paragraph marginBottom={24}>
        {t('buyWizard.postPaid.paymentFailureContactSupport1')}
        <AnchorOnClick
          onClick={(event) => {
            event.preventDefault();
            openSupport();
          }}
        >
          {t('buyWizard.postPaid.paymentFailureContactSupport2')}
        </AnchorOnClick>

        {t('buyWizard.postPaid.paymentFailureContactSupport3')}
      </Paragraph>
    </div>
  );

  const postPaidCCErrorMessage = () => (
    <div>
      <Paragraph marginBottom={24}>
        {t('buyWizard.postPaid.paymentFailureContactSupport1')}
        <AnchorOnClick
          onClick={(event) => {
            event.preventDefault();
            openSupport();
          }}
        >
          {t('buyWizard.postPaid.paymentFailureContactSupport2')}
        </AnchorOnClick>

        {t('buyWizard.postPaid.paymentFailureContactSupport3')}
      </Paragraph>
    </div>
  );

  const postPaidBraintreeErrorMessage = (errorMessage) => (
    <div>
      <Paragraph fullWidth marginBottom={12}>
        {errorMessage}
      </Paragraph>
      <Paragraph marginBottom={24}>
        {t('buyWizard.postPaid.paymentFailureContactSupport1')}
        <AnchorOnClick
          onClick={(event) => {
            event.preventDefault();
            openSupport();
          }}
        >
          {t('buyWizard.postPaid.paymentFailureContactSupport2')}
        </AnchorOnClick>

        {t('buyWizard.postPaid.paymentFailureContactSupport3')}
      </Paragraph>
    </div>
  );

  const postPaidPlaidErrorMessage = () => (
    <div>
      <Paragraph marginBottom={8}>
        {t('buyWizard.postPaid.plaidLoginErrorParagraph1')}
      </Paragraph>
      <Paragraph marginBottom={8} marginLeft={12}>
        <ul>
          <li>{t('buyWizard.postPaid.plaidLoginErrorFixList1')}</li>
          <li>{t('buyWizard.postPaid.plaidLoginErrorFixList2')}</li>
        </ul>
      </Paragraph>
      <Paragraph>
        {t('buyWizard.postPaid.plaidLoginErrorParagraph2')}
        <Paragraph marginBottom={24}>
          {t('buyWizard.postPaid.plaidLoginErrorParagraph3')}
          <AnchorOnClick
            onClick={(event) => {
              event.preventDefault();
              openSupport();
            }}
          >
            {t('buyWizard.postPaid.plaidLoginErrorParagraph4')}
          </AnchorOnClick>
        </Paragraph>
      </Paragraph>
    </div>
  );

  const onHandleExecuteQuotesError = (error) => {
    const errorCode = error?.response?.data?.Errors[0]?.Code;
    const errorMessage = error?.response?.data?.Errors[0]?.Message;
    if (postPaidApiErrors.includes(errorCode)) {
      if (errorCode === 'BraintreeTransactionError') {
        showError({
          title: t('buyWizard.postPaid.paymentFailureTitle'),
          message: postPaidBraintreeErrorMessage(errorMessage),
        });
        return;
      }
      if (errorCode === 'AvailableBalanceTooLow') {
        showError({
          title: t('buyWizard.postPaid.paymentFailureTitle'),
          message: errorMessage,
        });
        return;
      }
      if (errorCode === 'PlaidLoginRequired') {
        showError({
          title: t('buyWizard.postPaid.paymentFailureTitle'),
          message: postPaidPlaidErrorMessage(),
        });
        return;
      }
      if (
        selectedPaymentMethod?.PaymentMethodType === 3 &&
        errorCode !== 'PlaidLoginRequired'
      ) {
        showError({
          title: t('buyWizard.postPaid.paymentFailureTitle'),
          message: postPaidCCErrorMessage(),
        });
      } else {
        showError({
          title: t('buyWizard.postPaid.paymentFailureTitle'),
          message: postPaidErrorMessage(),
        });
      }
    } else {
      showError({
        title: t('apiErrors.IssueWithYourOrder'),
        message: t(`apiErrors.${errorCode}`),
      });
    }
  };

  const handleConfirmOrder = () => {
    const payload = {
      executeQuotePayload: {
        quoteUid: quoteUid || quoteID,
        signifydSessionId:
          isPostPaidVisible && selectedPaymentMethod?.PaymentMethodType !== 1
            ? sessionId
            : 'null',
      },
      isLoading: setIsLoadingExecuteQuotes,
      onSuccess: onHandleExecuteQuotesSuccess,
      onConfirm: onHandleSuccess,
      onError: onHandleExecuteQuotesError,
    };
    // setCloseIconVisible(false);
    setIsLoadingExecuteQuotes(true);
    dispatch(
      executeQuotesForOrderStorageBuy({
        ...payload,
      }),
    );
  };

  return (
    <>
      <SectionLoader
        isLoading={isLoadingGetQuotes || isLoadingExecuteQuotes}
        css={uLayoutStyle}
      >
        <BasicModalBody as={ModalLayout} style={{ position: 'relative' }}>
          <ProductInfoWrapper>
            <ProductImage src={encodeURI(imageSource)} alt="Product" />
            <ProductName>
              {t('buyWizard.buying')}
              <b>
                {quantityExecuted} x {storageProduct?.ShortCaption}
              </b>
            </ProductName>

            <PriceLockTimer
              counter={counter}
              unity={unity}
              loading={isLoadingGetQuotes}
            />
          </ProductInfoWrapper>
          <ScrollableMiddle>
            <OrderReviewPriceListWrapper>
              <OrderReviewPriceList
                paymentMethodInfo={paymentMethodInfo}
                paymentDiscount={paymentDiscount}
                calculation={calculation}
                onClick={handleOpenPriceTiers}
                selectedPaymentMethod={selectedPaymentMethod}
                paymentDiscountValue2={paymentDiscountValue2}
                isPostPaidVisible={isPostPaidVisible}
                totalPrice={totalPrice}
                quantityExecuted={quantityExecuted}
              />
            </OrderReviewPriceListWrapper>
          </ScrollableMiddle>
          {isPostPaidVisible && (
            <SavingInfoWrapper
              isCCSelected={selectedPaymentMethod?.PaymentMethodType === 3}
            >
              {selectedPaymentMethod?.PaymentMethodType === 3 ? (
                <>
                  <InfoIcon />
                  <Paragraph>
                    {t('buyWizard.reviewStepSavingInfo1')}
                    <b>
                      {formatMoneyNumeral(
                        paymentDiscount ?? paymentDiscountValue2,
                      )}
                    </b>
                  </Paragraph>
                </>
              ) : (
                <>
                  <HandCoinsIcon />
                  <Paragraph isCCSelected>
                    {t('buyWizard.reviewStepSavingInfo')}
                    <b>
                      {formatMoneyNumeral(
                        paymentDiscount ?? paymentDiscountValue2,
                      )}{' '}
                      (4%)
                    </b>
                  </Paragraph>
                </>
              )}
            </SavingInfoWrapper>
          )}

          <RowItemElement
            label={t('buyWizard.payWith')}
            value={
              <>
                {
                  PAYMENT_METHOD_TYPES_ICONS[
                    selectedPaymentMethod?.PaymentMethodType
                  ]
                }
                {paymentMethodRowItem()}
              </>
            }
            onClick={() => {
              if (isPostPaidVisible) {
                setOpenSelectPaymentOptions(true);
              } else {
                setIsFundsModalOpen(true);
                // setWizardContent(IS_FUND_MODAL_OPEN, true);
              }
            }}
            clickLabel={
              !isPostPaidVisible || !selectedPaymentMethod
                ? t('buyWizard.addFunds')
                : t('buyWizard.changeLabel')
            }
          />

          <ButtonWrapper>
            <ModalButtons
              isVertical
              showOnlyPrimary
              primaryButtonProps={{
                onClick: () => {
                  handleConfirmOrder();
                },
                label: t('buyWizard.buyNow'),
                textTransform: 'capitalize',
                marginTop: '10',
                'data-cy': 'button-confirm',
                disabled:
                  (selectedPaymentMethod?.PaymentMethodType === 2 &&
                    totalPrice() > selectedPaymentMethod?.MaximumLimit) ||
                  (selectedPaymentMethod?.PaymentMethodType === 3 &&
                    totalPrice() > selectedPaymentMethod?.MaximumLimit),
              }}
            />
          </ButtonWrapper>
          <ErrorDrawer
            open={isErrorOpen}
            errorTitle={errorTitle}
            errorMessage={errorMessage}
            onClose={hideError}
            openSupport={openSupport}
            closeSupport={closeSupport}
            isSupportOpen={isSupportOpen}
          />
        </BasicModalBody>
        {isFundsModalOpen && <BuyModalDimmer />}
      </SectionLoader>
      <SelectPaymentOptions
        open={openSelectPaymentOptions}
        setOpen={setOpenSelectPaymentOptions}
        wizardData={wizardData}
        setWizardData={setWizardData}
        paymentMethods={livePaymentMethods}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        setIsAddAddressOpen={setIsAddAddressOpen}
        isAddAddressOpen={isAddAddressOpen}
        accountUid={accountUid}
        setSuccessToast={setSuccessToast}
        isDeliveryOnlyAccount={isDeliveryOnlyAccount}
        setIsAddPaymentMethodOpen={setIsAddPaymentMethodOpen}
        isAddPaymentMethodOpen={isAddPaymentMethodOpen}
      />
      <PriceTiers
        open={openPriceTiersDrawer}
        setOpen={setOpenPriceTiersDrawer}
        productTiers={calculation?.productTiers}
        storageProduct={storageProduct}
        currentSpend={totalPrice()}
        selectedPaymentMethod={selectedPaymentMethod}
      />
      <SuccessToast
        isOpen={successToast.isOpen}
        title={successToast.title}
        message={successToast.message}
        onClose={() => setSuccessToast((prev) => ({ ...prev, isOpen: false }))}
      />
      {displayStandardOptionsPermission && isFundsModalOpen && (
        <StandardDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => {
            setIsFundsModalOpen(false);
            deleteWizardKey(STANDARD_DEPOSIT_WIZARD);
            deleteWizardKey(IS_FUND_MODAL_OPEN);
            deleteWizardKey(ACH_DEPOSIT_WIZARD);
          }}
          overModal
          avoidClearingStorage
        />
      )}

      {displayIRAEquityOptionsPermission && isFundsModalOpen && (
        <IraDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => {
            setIsFundsModalOpen(false);
            deleteWizardKey(IRA_DEPOSIT_WIZARD);
            deleteWizardKey(IS_FUND_MODAL_OPEN);
            deleteWizardKey(ACH_DEPOSIT_WIZARD);
          }}
          type={IRA_EQUITY_TYPE}
          overModal
          avoidClearingStorage
        />
      )}
      {displayIRAEntrustOptionsPermission && isFundsModalOpen && (
        <IraDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => {
            setIsFundsModalOpen(false);
            deleteWizardKey(IRA_DEPOSIT_WIZARD);
            deleteWizardKey(IS_FUND_MODAL_OPEN);
            deleteWizardKey(ACH_DEPOSIT_WIZARD);
          }}
          type={IRA_ENTRUST_TYPE}
          overModal
          avoidClearingStorage
        />
      )}
      {displayIRAMilleniumOptionsPermission && isFundsModalOpen && (
        <MilleniumDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => {
            setIsFundsModalOpen(false);
            deleteWizardKey(IRA_DEPOSIT_WIZARD);
            deleteWizardKey(IS_FUND_MODAL_OPEN);
            deleteWizardKey(ACH_DEPOSIT_WIZARD);
          }}
          overModal
          avoidClearingStorage
        />
      )}
    </>
  );
};

OrderReview.propTypes = {
  wizardData: PropTypes.shape({
    storageProduct: PropTypes.shape({
      ProductCaption: PropTypes.string,
      ProductPrice: PropTypes.number,
      Locations: PropTypes.arrayOf(
        PropTypes.shape({
          BuyMinimumAmount: PropTypes.number,
        }),
      ),
      ProductCode: PropTypes.string,
      ShortCaption: PropTypes.string,
      setWizardTitle: PropTypes.func,
    }),
    selectedPlan: PropTypes.string,
    activeType: PropTypes.string,
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      PricePerUnit: PropTypes.number,
      SymbolCode: PropTypes.string,
      HasDelayedSettlement: PropTypes.bool,
      BuyMinimumAmount: PropTypes.number,
    }),
    calculation: PropTypes.shape({
      estimatedOrderTotal: PropTypes.number,
      newPricePerUnit: PropTypes.number,
      volumeDiscount: PropTypes.number,
      quantity: PropTypes.number,
      paymentTypeDiscount: PropTypes.number,
      paymentMethodStepData: PropTypes.shape({}),
      salesTax: PropTypes.number,
      deliveryFee: PropTypes.number,
      productTiers: PropTypes.arrayOf({}),
    }),
    orderValues: PropTypes.shape({
      QuantityAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      CashAmount: PropTypes.number,
    }),
    quoteID: PropTypes.string,
    expiryTime: PropTypes.string,
    isVaultActiveTab: PropTypes.bool,
    selectedAddress: PropTypes.shape({
      AddressUid: PropTypes.string,
      StateRegion: PropTypes.string,
    }),
    updatedPaymentMethods: PropTypes.arrayOf({}),
    productTiers: PropTypes.arrayOf({}),
  }),
  setWizardData: PropTypes.func,
  isPostPaidVisible: PropTypes.bool,
  setWizardTitle: PropTypes.func,
  selectedPaymentMethod: PropTypes.shape({
    PaymentMethodType: PropTypes.number,
    PaymentMethodUid: PropTypes.number,
    Last4Digits: PropTypes.number,
    BankName: PropTypes.string,
    BankAccountMask: PropTypes.number,
    MaximumLimit: PropTypes.number,
  }),
  setSelectedPaymentMethod: PropTypes.func,
  isProductPage: PropTypes.bool,
  handleNext: PropTypes.func,
  setGoBackHandler: PropTypes.func,
  handleBack: PropTypes.func,
  isSegregated: PropTypes.bool,
  isDeliveryOnlyAccount: PropTypes.bool,
  openSupport: PropTypes.func,
  closeSupport: PropTypes.func,
  isSupportOpen: PropTypes.bool,
};

export default OrderReview;
